import React, { lazy, Suspense } from 'react';
import { Navigate, Outlet, ScrollRestoration } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { isPlyrmatchDomain } from './utils/portalDomain';
import PageLoadingFallback from './components/shared/PageLoadingFallback';
import RouteErrorBoundary from './components/shared/RouteErrorBoundary';
import PageShell from './components/layout/PageShell';
import AcademyPortalShell from './components/layout/AcademyPortalShell';
import PlyrmatchPortalShell from './components/layout/PlyrmatchPortalShell';

import Login from './pages/auth/Login';
import AcceptInvite from './pages/auth/AcceptInvite';
import Suspended from './pages/auth/Suspended';
const PlayerIntakeForm = lazy(() => import('./pages/intake/PlayerIntakeForm'));
const VisitRequestForm = lazy(() => import('./pages/visitor/VisitRequestForm'));
const RespondToVisitDate = lazy(() => import('./pages/visitor/RespondToVisitDate'));
const VisitFlightDetails = lazy(() => import('./pages/visitor/VisitFlightDetails'));
const ContractSigningPage = lazy(() => import('./pages/signing/ContractSigningPage'));
const AcademyContractSigningPage = lazy(() => import('./pages/signing/AcademyContractSigningPage'));
const ContractApprovalPage = lazy(() => import('./pages/signing/ContractApprovalPage'));
const ContractCoSignPage = lazy(() => import('./pages/signing/ContractCoSignPage'));
const IntakeFormPage = lazy(() => import('./pages/public/IntakeFormPage'));
const LeadFormPage = lazy(() => import('./pages/public/LeadFormPage'));
const TravelFormPage = lazy(() => import('./pages/visitor/TravelFormPage'));
const PlayerCvPage = lazy(() => import('./pages/public/PlayerCvPage'));
const PlayerIdPage = lazy(() => import('./pages/public/PlayerIdPage'));
const AcademyPlayerStatsPage = lazy(() => import('./pages/public/AcademyPlayerStatsPage'));
const JoinIAP = lazy(() => import('./pages/landing/JoinIAP'));
const JoinWithCode = lazy(() => import('./pages/auth/JoinWithCode'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const MentorshipAdmin = lazy(() => import('./pages/admin/mentorship/MentorshipAdmin'));
const PlyrmatchMentorship = lazy(() => import('./pages/plyrmatch/Mentorship'));
const AcademyPortalLanding = lazy(() => import('./pages/academyportal/AcademyPortalLanding'));
const PortalsLanding = lazy(() => import('./pages/marketing/portals/PortalsLanding'));
const CrmLanding = lazy(() => import('./pages/marketing/crm/CrmLanding'));
const OnboardingLanding = lazy(() => import('./pages/marketing/onboarding/OnboardingLanding'));
const HerculesWebsite = lazy(() => import('./pages/public/HerculesWebsite'));
const HerculesTrialPage = lazy(() => import('./pages/public/HerculesTrialPage'));
const HerculesTourPage = lazy(() => import('./pages/public/HerculesTourPage'));
const PrivacyPage = lazy(() => import('./pages/landing/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/landing/TermsPage'));
const LeadBotChatPage = lazy(() => import('./pages/public/LeadBotChatPage'));
const LeadBotBookingPage = lazy(() => import('./pages/public/LeadBotBookingPage'));
const BookingLinkPage = lazy(() => import('./pages/public/BookingLinkPage'));

// Admin
const AdminCalendar = lazy(() => import('./pages/admin/Calendar'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const Contracts = lazy(() => import('./pages/admin/Contracts'));
const ContractDetail = lazy(() => import('./pages/admin/ContractDetail'));
const NewContract = lazy(() => import('./pages/admin/NewContract'));
const ContractTemplates = lazy(() => import('./pages/admin/ContractTemplates'));
const ContractTemplateDetail = lazy(() => import('./pages/admin/ContractTemplateDetail'));
const ContractTemplateGuide = lazy(() => import('./pages/admin/ContractTemplateGuide'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const Help = lazy(() => import('./pages/admin/Help'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const PlayersList = lazy(() => import('./pages/admin/PlayersList'));
const PlayerDetail = lazy(() => import('./pages/admin/PlayerDetail'));
const PlayerPortalPreview = lazy(() => import('./pages/admin/PlayerPortalPreview'));
const PlayerProfileEditor = lazy(() => import('./pages/admin/PlayerProfileEditor'));
const NewPlayer = lazy(() => import('./pages/admin/NewPlayer'));
const CustomFields = lazy(() => import('./pages/admin/CustomFields'));
const AuditLog = lazy(() => import('./pages/admin/AuditLog'));
const QuotesInvoices = lazy(() => import('./pages/admin/QuotesInvoices'));
const NewQuote = lazy(() => import('./pages/admin/NewQuote'));
const QuoteDetail = lazy(() => import('./pages/admin/QuoteDetail'));
const InvoiceDetail = lazy(() => import('./pages/admin/InvoiceDetail'));
const NewInvoiceDirect = lazy(() => import('./pages/admin/NewInvoiceDirect'));
const EmailTemplateDetail = lazy(() => import('./pages/admin/EmailTemplateDetail'));
const Automation = lazy(() => import('./pages/admin/Automation'));
const AutomationWorkflowDetail = lazy(() => import('./pages/admin/AutomationWorkflowDetail'));
const Visits = lazy(() => import('./pages/admin/Visits'));
const VisitDetail = lazy(() => import('./pages/admin/VisitDetail'));
const TravelFormBatch = lazy(() => import('./pages/admin/TravelFormBatch'));
const AdminMessages = lazy(() => import('./pages/admin/Messages'));
const AskAssistant = lazy(() => import('./pages/admin/AskAssistant'));

// CRM (pre-sales)
const CrmDashboard = lazy(() => import('./pages/admin/crm/CrmDashboard'));
const LeadsList = lazy(() => import('./pages/admin/crm/LeadsList'));
const NewLead = lazy(() => import('./pages/admin/crm/NewLead'));
const ImportLeads = lazy(() => import('./pages/admin/crm/ImportLeads'));
const LeadDetail = lazy(() => import('./pages/admin/crm/LeadDetail'));
const CrmQuotes = lazy(() => import('./pages/admin/crm/CrmQuotes'));
const CrmTasks = lazy(() => import('./pages/admin/crm/Tasks'));
const Communications = lazy(() => import('./pages/admin/crm/Communications'));
const Partners = lazy(() => import('./pages/admin/crm/Partners'));
const LeadBot = lazy(() => import('./pages/admin/crm/leadbot/LeadBot'));
const Bookings = lazy(() => import('./pages/admin/crm/bookings/Bookings'));

// Super admin
const SuperTenants = lazy(() => import('./pages/super/Tenants'));
const SuperTenantDetail = lazy(() => import('./pages/super/TenantDetail'));
const SuperCreateAcademy = lazy(() => import('./pages/super/CreateAcademy'));
const SuperSettings = lazy(() => import('./pages/super/SuperSettings'));
const SuperSalesStaff = lazy(() => import('./pages/super/SalesStaff'));
const SuperChatsInbox = lazy(() => import('./pages/super/ChatsInbox'));
const SuperSalesResources = lazy(() => import('./pages/super/SalesResources'));
const SuperTrialCodes = lazy(() => import('./pages/super/TrialCodes'));
const RegisterPage = lazy(() => import('./pages/marketing/register/RegisterPage'));
const SuperHelpArticles = lazy(() => import('./pages/super/HelpArticles'));
const SuperHelpArticleEditor = lazy(() => import('./pages/super/HelpArticleEditor'));
const SuperHelpQuestions = lazy(() => import('./pages/super/HelpQuestions'));

// IAP Academy Sales CRM — separate, IAP-internal pipeline for tracking
// prospective academies as leads (see academyLeadStages.js).
const AcademyLeads = lazy(() => import('./pages/super/AcademyLeads'));
const NewAcademyLead = lazy(() => import('./pages/super/NewAcademyLead'));
const AcademyLeadDetail = lazy(() => import('./pages/super/AcademyLeadDetail'));
const AcademyFinance = lazy(() => import('./pages/super/AcademyFinance'));
const AcademyContractTemplates = lazy(() => import('./pages/super/AcademyContractTemplates'));
const AcademyContractTemplateDetail = lazy(() => import('./pages/super/AcademyContractTemplateDetail'));

// Academy Portal (coaches + players, at-academy)
const ChoosePortal = lazy(() => import('./pages/portal/ChoosePortal'));
const AcademyHome = lazy(() => import('./pages/academy/Home'));
const AcademyGroups = lazy(() => import('./pages/academy/Groups'));
const AcademyGroupDetail = lazy(() => import('./pages/academy/GroupDetail'));
const AcademyMessages = lazy(() => import('./pages/academy/Messages'));
const AcademyMessageThread = lazy(() => import('./pages/academy/MessageThread'));
const AcademyFeed = lazy(() => import('./pages/academy/Feed'));
const AcademyCalendar = lazy(() => import('./pages/academy/Calendar'));
const AcademyDocuments = lazy(() => import('./pages/academy/Documents'));
const AcademyReports = lazy(() => import('./pages/academy/Reports'));
const AcademyPrograms = lazy(() => import('./pages/academy/Programs'));
const AcademyClubSettings = lazy(() => import('./pages/academy/ClubSettings'));
const AcademyProfile = lazy(() => import('./pages/academy/Profile'));
const AcademyPlayerStats = lazy(() => import('./pages/academy/PlayerStats'));

// Guardian
const GuardianDashboard = lazy(() => import('./pages/guardian/Dashboard'));
const GuardianProfile = lazy(() => import('./pages/guardian/Profile'));
const GuardianDocuments = lazy(() => import('./pages/guardian/Documents'));
const GuardianFinances = lazy(() => import('./pages/guardian/Finances'));
const GuardianContracts = lazy(() => import('./pages/guardian/Contracts'));
const GuardianTravel = lazy(() => import('./pages/guardian/Travel'));
const GuardianMessaging = lazy(() => import('./pages/guardian/Messaging'));
const GuardianCalendar = lazy(() => import('./pages/guardian/Calendar'));

// PLYRMATCH Portal — agency-sourced players only (see AuthContext's `access`
// claim 'plyrmatchPortal', granted at lead conversion — createPlayerRecordWithGuardianInvite)
const PlyrmatchDashboard = lazy(() => import('./pages/plyrmatch/Dashboard'));
const PlyrmatchPlayerProfile = lazy(() => import('./pages/plyrmatch/PlayerProfile'));
const PlyrmatchPlayerStats = lazy(() => import('./pages/plyrmatch/PlayerStats'));
const PlyrmatchAcademyHub = lazy(() => import('./pages/plyrmatch/AcademyHub'));
const PlyrmatchAcademyDetail = lazy(() => import('./pages/plyrmatch/AcademyDetail'));
const PlyrmatchNextSteps = lazy(() => import('./pages/plyrmatch/NextSteps'));
const PlyrmatchMessages = lazy(() => import('./pages/plyrmatch/Messages'));
const PlyrmatchSettings = lazy(() => import('./pages/plyrmatch/Settings'));

// Top-level routes (public forms, landing pages, the portal chooser) sit
// outside PageShell/AcademyPortalShell, so they don't inherit either
// shell's Suspense boundary around Outlet — wrap each individually instead.
function withSuspense(element) {
  return <Suspense fallback={<PageLoadingFallback />}>{element}</Suspense>;
}

function RequireAuth({ allowedRoles, requiredAccess }) {
  const { user, role, access, loading, academySuspended } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-gray-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(role)) return <Navigate to="/login" replace />;
  // requiredAccess is either a single section or an array meaning "any of
  // these" — used for pages reachable from more than one mode (e.g.
  // Contracts, shared between CRM and Onboarding — see SHARED_PATHS in
  // Sidebar.jsx).
  if (requiredAccess) {
    const required = Array.isArray(requiredAccess) ? requiredAccess : [requiredAccess];
    if (!required.some((a) => access.includes(a))) return <Navigate to="/login" replace />;
  }
  if (academySuspended && (role === 'academyAdmin' || role === 'salesRep')) return <Suspended />;
  return <Outlet />;
}

// A coach's whole account is scoped to the Academy Portal (no "access" claim
// to check); a guardian-role account needs the academyPortal claim granted
// explicitly; an academyAdmin always gets in too, same as they do for CRM
// and onboarding — Firestore rules already give them full academy-wide
// access to groups/conversations/posts/calendarEvents via belongsToAcademy.
// Can't express this as separate RequireAuth blocks sharing the same
// /academy/* paths — react-router only ever matches the first of two routes
// with an identical path, silently orphaning the second — so it's one guard
// with an OR condition instead.
function RequireAcademyPortal() {
  const { user, role, access, loading, canManageAcademyPortal } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen text-gray-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // canManageAcademyPortal already excludes an agency tenant and a partner
  // academy whose portalAccess.academyPortal is still false (see
  // AuthContext) — an academyAdmin doesn't get an automatic pass here the
  // way they used to; this was previously reachable by any academyAdmin
  // regardless, only ever hidden from the nav.
  const allowed = role === 'coach' || (role === 'academyAdmin' && canManageAcademyPortal) || (role === 'guardian' && access.includes('academyPortal'));
  if (!allowed) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function RedirectByRole() {
  const { user, role, access, loading, canManageAcademyPortal } = useAuth();
  if (loading) return null;
  if (role === 'superAdmin' || role === 'iapSales') return <Navigate to="/super/tenants" replace />;
  if (role === 'academyAdmin' || role === 'salesRep') {
    // Whoever has both sections lands on CRM first — it's the front door
    // for new business, onboarding only starts once a lead converts.
    if (access.includes('crm')) return <Navigate to="/admin/crm/dashboard" replace />;
    if (access.includes('onboarding')) return <Navigate to="/admin/dashboard" replace />;
    // IAP can license a tenant for the Academy Portal alone.
    if (role === 'academyAdmin' && canManageAcademyPortal) return <Navigate to="/academy/home" replace />;
    return <Navigate to="/login" replace />;
  }
  if (role === 'coach') return <Navigate to="/academy/home" replace />;
  if (role === 'guardian') {
    const hasAcademy = access.includes('academyPortal');
    const hasPlayer = access.includes('playerPortal');
    const hasPlyrmatch = access.includes('plyrmatchPortal');
    // The PLYRMATCH domain only serves this portal, so no chooser here.
    if (hasPlyrmatch && isPlyrmatchDomain()) return <Navigate to="/plyrmatch/dashboard" replace />;
    if ([hasAcademy, hasPlayer, hasPlyrmatch].filter(Boolean).length > 1) {
      // Keyed per-account — the same key unscoped would leak whichever
      // portal the LAST account tested in this browser landed on (this
      // browser's `portalMode` is also reused by staff for the CRM/
      // Onboarding toggle, see Sidebar.jsx), silently skipping the chooser.
      let stored;
      try { stored = localStorage.getItem(`portalMode:${user.uid}`); } catch { stored = null; }
      if (stored === 'academyPortal' && hasAcademy) return <Navigate to="/academy/home" replace />;
      if (stored === 'playerPortal' && hasPlayer) return <Navigate to="/portal/dashboard" replace />;
      if (stored === 'plyrmatchPortal' && hasPlyrmatch) return <Navigate to="/plyrmatch/dashboard" replace />;
      return <Navigate to="/portal/choose" replace />;
    }
    if (hasAcademy) return <Navigate to="/academy/home" replace />;
    if (hasPlyrmatch) return <Navigate to="/plyrmatch/dashboard" replace />;
    return <Navigate to="/portal/dashboard" replace />;
  }
  return <Navigate to="/login" replace />;
}

// A single pathless wrapper so every route below shares one errorElement — a
// dynamic-import failure (e.g. a stale tab after a deploy) otherwise falls
// through to react-router's own generic error screen instead of anything
// this app defines. See RouteErrorBoundary.
//
// ScrollRestoration lives here (rather than as an implicit Outlet) so every
// route gets its scroll position reset on a fresh navigation and correctly
// restored on back/forward — without it, the data router leaves whatever
// scrollY the previous page had, so a page visited after scrolling another
// one to the bottom would otherwise render starting at the bottom too.
export const routes = [
  {
    errorElement: <RouteErrorBoundary />,
    element: <><ScrollRestoration /><Outlet /></>,
    children: [
  { path: '/', element: <RedirectByRole /> },
  { path: '/joinIAP', element: withSuspense(<JoinIAP />) },
  // Marketing site: /portals is the overview + pricing console, then one
  // full page per portal (all in src/pages/marketing + academyportal).
  { path: '/portals', element: withSuspense(<PortalsLanding />) },
  { path: '/register', element: withSuspense(<RegisterPage />) },
  { path: '/pricing', element: <Navigate to="/portals#pricing" replace /> },
  { path: '/crmportal', element: withSuspense(<CrmLanding />) },
  { path: '/onboardingportal', element: withSuspense(<OnboardingLanding />) },
  { path: '/academyportal', element: withSuspense(<AcademyPortalLanding />) },
  { path: '/herculeswebsite', element: withSuspense(<HerculesWebsite />) },
  // Website lead bot — the chat (embedded in academies' sites via
  // public/leadbot.js, or as a direct link) and the manage-booking page.
  { path: '/leadbot/booking/:token', element: withSuspense(<LeadBotBookingPage />) },
  { path: '/leadbot/:publicKey', element: withSuspense(<LeadBotChatPage />) },
  // Booking links — the academy's team page and each person's own page.
  { path: '/book/:slug', element: withSuspense(<BookingLinkPage />) },
  { path: '/book/:slug/:person', element: withSuspense(<BookingLinkPage />) },
  { path: '/book/:slug/:person/:type', element: withSuspense(<BookingLinkPage />) },
  { path: '/herculeswebsite/trial', element: withSuspense(<HerculesTrialPage />) },
  { path: '/herculeswebsite/tour', element: withSuspense(<HerculesTourPage />) },
  { path: '/privacy', element: withSuspense(<PrivacyPage />) },
  { path: '/terms', element: withSuspense(<TermsPage />) },
  { path: '/login', element: <Login /> },
  { path: '/reset-password', element: withSuspense(<ResetPassword />) },
  { path: '/invite/:token', element: <AcceptInvite /> },
  // Self-registration with a group's join code (GroupJoinCodeCard.jsx).
  { path: '/join', element: withSuspense(<JoinWithCode />) },
  { path: '/join/:code', element: withSuspense(<JoinWithCode />) },
  { path: '/intake/:token', element: withSuspense(<PlayerIntakeForm />) },
  { path: '/visit/:academyId', element: withSuspense(<VisitRequestForm />) },
  { path: '/visit-response/:token', element: withSuspense(<RespondToVisitDate />) },
  { path: '/visit-flight/:token',   element: withSuspense(<VisitFlightDetails />) },
  { path: '/sign/:token', element: withSuspense(<ContractSigningPage />) },
  { path: '/academy-sign/:token', element: withSuspense(<AcademyContractSigningPage />) },
  { path: '/approve/:token', element: withSuspense(<ContractApprovalPage />) },
  { path: '/cosign/:token', element: withSuspense(<ContractCoSignPage />) },
  { path: '/intake-form/:token', element: withSuspense(<IntakeFormPage />) },
  // Website enquiry forms (Settings → Embed a form; public/forms.js embeds it).
  { path: '/f/:slug', element: withSuspense(<LeadFormPage />) },
  { path: '/travel-form/:token', element: withSuspense(<TravelFormPage />) },
  { path: '/cv/:playerId/:token', element: withSuspense(<PlayerCvPage />) },
  { path: '/id/:playerId/:token', element: withSuspense(<PlayerIdPage />) },
  { path: '/stats/:playerId/:token', element: withSuspense(<AcademyPlayerStatsPage />) },

  {
    // Coaches get a fixed slice of Onboarding (Calendar + Players — see
    // DEFAULT_PERMISSIONS.coach) alongside full Academy Portal access; the
    // rest of these routes are simply absent from their nav (Sidebar.jsx).
    element: <RequireAuth allowedRoles={['academyAdmin', 'salesRep', 'coach']} requiredAccess="onboarding" />,
    children: [
      {
        element: <PageShell />,
        children: [
          { path: '/admin/dashboard',        element: <AdminDashboard />,  handle: { title: 'common:routeTitles.dashboard' } },
          { path: '/admin/calendar',         element: <AdminCalendar />,   handle: { title: 'common:routeTitles.calendar' } },
          { path: '/admin/mentorship',        element: <MentorshipAdmin />, handle: { title: 'common:routeTitles.mentorship' } },
          { path: '/admin/players',           element: <PlayersList />,     handle: { title: 'common:routeTitles.players' } },
          { path: '/admin/players/new',       element: <NewPlayer />,       handle: { title: 'common:routeTitles.addPlayer' } },
          { path: '/admin/players/:id',       element: <PlayerDetail />,    handle: { title: 'common:routeTitles.playerProfile' } },
          { path: '/admin/players/:id/preview', element: <PlayerPortalPreview />, handle: { title: 'common:routeTitles.portalPreview' } },
          { path: '/admin/players/:id/profile', element: <PlayerProfileEditor />, handle: { title: 'common:routeTitles.playerProfile' } },
          { path: '/admin/contract-templates',       element: <ContractTemplates />,      handle: { title: 'common:routeTitles.contractTemplates' } },
          { path: '/admin/contract-templates/guide', element: <ContractTemplateGuide />,  handle: { title: 'common:routeTitles.contractGuide' } },
          { path: '/admin/contract-templates/:id',   element: <ContractTemplateDetail />, handle: { title: 'common:routeTitles.contractTemplate' } },
          { path: '/admin/quotes',            element: <QuotesInvoices />,  handle: { title: 'common:routeTitles.quotes' } },
          { path: '/admin/quotes/new',        element: <NewQuote />,        handle: { title: 'common:routeTitles.newQuote' } },
          { path: '/admin/quotes/:id',        element: <QuoteDetail />,     handle: { title: 'common:routeTitles.quote' } },
          { path: '/admin/email-templates/:id', element: <EmailTemplateDetail />, handle: { title: 'common:routeTitles.emailTemplate' } },
          { path: '/admin/automation',        element: <Automation />,            handle: { title: 'common:routeTitles.automation' } },
          { path: '/admin/automation/:id',    element: <AutomationWorkflowDetail />, handle: { title: 'common:routeTitles.automationWorkflow' } },
          { path: '/admin/visits',            element: <Visits />,                handle: { title: 'common:routeTitles.visits' } },
          { path: '/admin/visits/:id',        element: <VisitDetail />,           handle: { title: 'common:routeTitles.visit' } },
          { path: '/admin/travel-forms/:batchId', element: <TravelFormBatch />,   handle: { title: 'common:routeTitles.travelFormBatch' } },
          { path: '/admin/messages',          element: <AdminMessages />,         handle: { title: 'common:routeTitles.messages' } },
          { path: '/admin/assistant',         element: <AskAssistant />,          handle: { title: 'common:routeTitles.assistant' } },
          { path: '/admin/custom-fields',     element: <CustomFields />,          handle: { title: 'common:routeTitles.customFields' } },
          { path: '/admin/audit-log',         element: <AuditLog />,        handle: { title: 'common:routeTitles.auditLog' } },
          { path: '/admin/users',             element: <AdminUsers />,      handle: { title: 'common:routeTitles.userManagement' } },
          { path: '/admin/settings',          element: <AdminSettings />,   handle: { title: 'common:routeTitles.academySettings' } },
        ],
      },
    ],
  },

  {
    // CRM (pre-sales) mode — academy admins see all leads, sales reps see
    // only their own (enforced by Firestore rules, not just this route guard).
    // requiredAccess="crm" is what actually keeps a partner academy (or any
    // staff member without it — see AuthContext's access/portalAccess) out
    // of these routes by URL, not just off the nav — previously missing
    // here (unlike the onboarding group below), so this was reachable
    // directly regardless of access.
    element: <RequireAuth allowedRoles={['academyAdmin', 'salesRep']} requiredAccess="crm" />,
    children: [
      {
        element: <PageShell />,
        children: [
          { path: '/admin/crm/dashboard',   element: <CrmDashboard />, handle: { title: 'common:routeTitles.crmDashboard' } },
          { path: '/admin/crm/calendar',    element: <AdminCalendar />, handle: { title: 'common:routeTitles.calendar' } },
          { path: '/admin/crm/leads',       element: <LeadsList />,    handle: { title: 'common:routeTitles.leads' } },
          { path: '/admin/crm/leads/new',   element: <NewLead />,      handle: { title: 'common:routeTitles.newLead' } },
          { path: '/admin/crm/leads/import', element: <ImportLeads />, handle: { title: 'common:routeTitles.importLeads' } },
          { path: '/admin/crm/leads/:id',   element: <LeadDetail />,   handle: { title: 'common:routeTitles.lead' } },
          { path: '/admin/crm/quotes',      element: <CrmQuotes />,    handle: { title: 'common:routeTitles.quotes' } },
          { path: '/admin/crm/quotes/new',  element: <NewQuote />,     handle: { title: 'common:routeTitles.newQuote' } },
          { path: '/admin/crm/quotes/:id',  element: <QuoteDetail />,  handle: { title: 'common:routeTitles.quote' } },
          { path: '/admin/crm/tasks',       element: <CrmTasks />,     handle: { title: 'common:routeTitles.tasks' } },
          { path: '/admin/crm/video-calls', element: <Navigate to="/admin/crm/bookings/video" replace /> },
          { path: '/admin/crm/partners',    element: <Partners />,    handle: { title: 'common:routeTitles.partners' } },
          { path: '/admin/crm/lead-bot',      element: <LeadBot />,     handle: { title: 'common:routeTitles.leadBot' } },
          { path: '/admin/crm/lead-bot/:tab', element: <LeadBot />,     handle: { title: 'common:routeTitles.leadBot' } },
          { path: '/admin/crm/bookings',      element: <Bookings />,    handle: { title: 'common:routeTitles.bookings' } },
          { path: '/admin/crm/bookings/:tab', element: <Bookings />,    handle: { title: 'common:routeTitles.bookings' } },
          { path: '/admin/crm/consultants', element: <Navigate to="/admin/users" replace /> },
        ],
      },
    ],
  },

  {
    // Contracts, Invoices and Communications — shared between CRM (a lead's
    // contract, a lead's fee invoice, a lead's message log) and Onboarding
    // (a placed player's contract — see PLYRMATCH's "Send this academy's
    // contract" panel; a player's own invoices; a player's message log —
    // all already linked from adminNav in Sidebar.jsx, see SHARED_PATHS
    // there), so either access unlocks them. Split out from the CRM-only
    // group above so a partner academy (onboarding-only access, no crm)
    // still reaches them — Communications was the one missed when
    // Contracts was first split out, which is what broke it; Invoices was
    // added here when an agency tenant's CRM-mode "Invoices" nav item was
    // added (a crm-only sales rep needs it too, not just academyAdmin).
    element: <RequireAuth allowedRoles={['academyAdmin', 'salesRep']} requiredAccess={['crm', 'onboarding']} />,
    children: [
      {
        element: <PageShell />,
        children: [
          { path: '/admin/contracts',       element: <Contracts />,      handle: { title: 'common:routeTitles.contracts' } },
          { path: '/admin/contracts/new',   element: <NewContract />,    handle: { title: 'common:routeTitles.newContract' } },
          { path: '/admin/contracts/:id',   element: <ContractDetail />, handle: { title: 'common:routeTitles.contract' } },
          { path: '/admin/invoices',        element: <QuotesInvoices />,   handle: { title: 'common:routeTitles.invoices' } },
          { path: '/admin/invoices/new',    element: <NewInvoiceDirect />, handle: { title: 'common:routeTitles.newInvoice' } },
          { path: '/admin/invoices/:id',    element: <InvoiceDetail />,    handle: { title: 'common:routeTitles.invoice' } },
          { path: '/admin/crm/communications', element: <Communications />, handle: { title: 'common:routeTitles.communications' } },
        ],
      },
    ],
  },

  {
    // Help Centre — reachable from both CRM and Onboarding (the Topbar's Help
    // panel opens the same thing; see components/help/HelpButton.jsx).
    element: <RequireAuth allowedRoles={['academyAdmin', 'salesRep', 'coach']} requiredAccess={['crm', 'onboarding']} />,
    children: [
      {
        element: <PageShell />,
        children: [
          { path: '/admin/help', element: <Help />, handle: { title: 'common:routeTitles.helpSupport' } },
        ],
      },
    ],
  },

  {
    // iapSales (IAP's own platform-level sales staff, not an academy's own
    // salesRep) shares these routes with superAdmin — add new
    // academies/agencies, see the tenant list, the Academy Sales CRM, and
    // (as of the support-chat access extension) the support chat inbox.
    // Everything else under /super/* (tenant detail/management, Settings,
    // the Sales Staff page itself) stays superAdmin-only in the block below.
    element: <RequireAuth allowedRoles={['superAdmin', 'iapSales']} />,
    children: [
      {
        element: <PageShell />,
        children: [
          { path: '/super/tenants',        element: <SuperTenants />,        handle: { title: 'common:routeTitles.academies' } },
          { path: '/super/create-academy', element: <SuperCreateAcademy />,  handle: { title: 'common:routeTitles.createAcademy' } },
          { path: '/super/leads',          element: <AcademyLeads />,        handle: { title: 'Academy Leads' } },
          { path: '/super/leads/new',      element: <NewAcademyLead />,      handle: { title: 'New Academy Lead' } },
          { path: '/super/leads/:id',      element: <AcademyLeadDetail />,   handle: { title: 'Academy Lead' } },
          { path: '/super/finance',        element: <AcademyFinance />,      handle: { title: 'Finance' } },
          { path: '/super/contract-templates',     element: <AcademyContractTemplates />,       handle: { title: 'Contract Templates' } },
          // A single :id route, same as /admin/contract-templates/:id — visiting
          // .../new makes useParams().id === 'new' via the param itself. A separate
          // literal /new route here previously had no :id param at all, so id was
          // undefined and isNew (id === 'new') was false, sending the "new" page
          // straight into edit mode and crashing on getDoc(..., undefined).
          { path: '/super/contract-templates/:id', element: <AcademyContractTemplateDetail />,  handle: { title: 'Contract Template' } },
          { path: '/super/chats',          element: <SuperChatsInbox />,     handle: { title: 'common:routeTitles.supportChats' } },
          { path: '/super/resources',      element: <SuperSalesResources />, handle: { title: 'Resources' } },
          { path: '/super/trial-codes',    element: <SuperTrialCodes />,     handle: { title: 'Free Trial Codes' } },
        ],
      },
    ],
  },

  {
    element: <RequireAuth allowedRoles={['superAdmin']} />,
    children: [
      {
        element: <PageShell />,
        children: [
          { path: '/super/tenants/:id',    element: <SuperTenantDetail />,   handle: { title: 'common:routeTitles.academy' } },
          { path: '/super/settings',       element: <SuperSettings />,       handle: { title: 'common:routeTitles.platformSettings' } },
          { path: '/super/staff',          element: <SuperSalesStaff />,     handle: { title: 'common:routeTitles.salesStaff' } },
          { path: '/super/help',           element: <SuperHelpArticles />,   handle: { title: 'Help Centre' } },
          { path: '/super/help/questions', element: <SuperHelpQuestions />,  handle: { title: 'Help Centre' } },
          { path: '/super/help/:id',       element: <SuperHelpArticleEditor />, handle: { title: 'Help article' } },
        ],
      },
    ],
  },

  {
    element: <RequireAcademyPortal />,
    children: [
      { path: '/portal/choose', element: withSuspense(<ChoosePortal />) },
      {
        element: <AcademyPortalShell />,
        children: [
          { path: '/academy/home',              element: <AcademyHome />,          handle: { title: 'common:routeTitles.home' } },
          { path: '/academy/groups',             element: <AcademyGroups />,        handle: { title: 'common:routeTitles.groups' } },
          { path: '/academy/groups/:id',         element: <AcademyGroupDetail />,   handle: { title: 'common:routeTitles.group' } },
          { path: '/academy/messages',           element: <AcademyMessages />,      handle: { title: 'common:routeTitles.messages' } },
          { path: '/academy/messages/:id',       element: <AcademyMessageThread />, handle: { title: 'common:routeTitles.conversation' } },
          { path: '/academy/feed',               element: <AcademyFeed />,          handle: { title: 'common:routeTitles.feed' } },
          { path: '/academy/calendar',           element: <AcademyCalendar />,      handle: { title: 'common:routeTitles.calendar' } },
          { path: '/academy/documents',          element: <AcademyDocuments />,     handle: { title: 'common:routeTitles.documents' } },
          { path: '/academy/reports',            element: <AcademyReports />,       handle: { title: 'common:routeTitles.reports' } },
          { path: '/academy/programs',           element: <AcademyPrograms />,      handle: { title: 'common:routeTitles.programs' } },
          { path: '/academy/club',               element: <AcademyClubSettings />,  handle: { title: 'academy:clubSettings.pageTitle' } },
          { path: '/academy/profile',            element: <AcademyProfile />,       handle: { title: 'common:routeTitles.profile' } },
          { path: '/academy/stats',              element: <AcademyPlayerStats />,   handle: { title: 'common:routeTitles.stats' } },
          { path: '/academy/players/:playerId/stats', element: <AcademyPlayerStats />, handle: { title: 'common:routeTitles.stats' } },
          // Same AskAssistant component as /admin/assistant, just mounted
          // inside AcademyPortalShell so a coach tapping it from their
          // mobile app stays inside the Academy Portal shell/nav instead of
          // being dropped into the separate Onboarding portal.
          { path: '/academy/assistant',          element: <AskAssistant />,         handle: { title: 'common:routeTitles.assistant' } },
        ],
      },
    ],
  },

  {
    element: <RequireAuth allowedRoles={['guardian']} />,
    children: [
      {
        element: <PageShell />,
        children: [
          { path: '/portal/dashboard',  element: <GuardianDashboard />,  handle: { title: 'common:routeTitles.myChecklist' } },
          { path: '/portal/profile',    element: <GuardianProfile />,    handle: { title: 'common:routeTitles.playerProfile' } },
          { path: '/portal/documents',  element: <GuardianDocuments />,  handle: { title: 'common:routeTitles.documents' } },
          { path: '/portal/invoices',   element: <Navigate to="/portal/finances" replace /> },
          { path: '/portal/finances',   element: <GuardianFinances />,   handle: { title: 'common:routeTitles.finances' } },
          { path: '/portal/contracts',  element: <GuardianContracts />,  handle: { title: 'common:routeTitles.contracts' } },
          { path: '/portal/travel',     element: <GuardianTravel />,     handle: { title: 'common:routeTitles.travelForms' } },
          { path: '/portal/messages',   element: <GuardianMessaging />,  handle: { title: 'common:routeTitles.messages' } },
          { path: '/portal/calendar',   element: <GuardianCalendar />,   handle: { title: 'common:routeTitles.calendar' } },
        ],
      },
    ],
  },

  {
    element: <RequireAuth allowedRoles={['guardian']} requiredAccess="plyrmatchPortal" />,
    children: [
      {
        element: <PlyrmatchPortalShell />,
        children: [
          { path: '/plyrmatch/dashboard', element: <PlyrmatchDashboard />,     handle: { title: 'common:routeTitles.dashboard' } },
          { path: '/plyrmatch/profile',   element: <PlyrmatchPlayerProfile />, handle: { title: 'common:routeTitles.playerProfile' } },
          { path: '/plyrmatch/stats',     element: <PlyrmatchPlayerStats />,   handle: { title: 'plyrmatch:nav.stats' } },
          { path: '/plyrmatch/academies', element: <PlyrmatchAcademyHub />,    handle: { title: 'plyrmatch:nav.academyHub' } },
          { path: '/plyrmatch/academies/:id', element: <PlyrmatchAcademyDetail />, handle: { title: 'plyrmatch:nav.academyHub' } },
          { path: '/plyrmatch/mentorship', element: <PlyrmatchMentorship />,   handle: { title: 'common:routeTitles.mentorship' } },
          { path: '/plyrmatch/next-steps', element: <PlyrmatchNextSteps />,    handle: { title: 'plyrmatch:nav.nextSteps' } },
          { path: '/plyrmatch/messages',   element: <PlyrmatchMessages />,     handle: { title: 'common:routeTitles.messages' } },
          { path: '/plyrmatch/settings',  element: <PlyrmatchSettings />,      handle: { title: 'common:actions.settings' } },
        ],
      },
    ],
  },
    ],
  },
];
